import { useCallback, useEffect, useRef, useState } from "react";
import type { ContentBundle, TaskDefinition } from "@talent/protocol";
import { MemoryOutboxStore, SessionRecorder } from "@talent/client-telemetry";
import { getBundle } from "@talent/tasks";
import { DiscardingTransport } from "../api.js";
import { COPY, fill, strandLabel, taskLabel } from "../copy.js";
import { ScreenHeader } from "./screen-header.js";
import { APP_VERSION } from "../env.js";
import { runGame } from "../game-runners.js";
import { HomeScreen } from "../home-screen.js";
import { nextForm } from "../profile.js";
import { latestBundleVersion } from "../site/shelf.js";
import { TutorialPrompt, type TutorialChoice } from "../tutorial-prompt.js";
import type { Route } from "../route.js";

/**
 * The games, as a teacher sees them.
 *
 * Asked for so a teacher can explain a game to a class, which means it has to be the REAL game — a
 * mock-up would let them explain something the child does not see. So it runs the same renderers, the
 * same generators and the same difficulty ladder.
 *
 * **Nothing is recorded, and that is structural rather than a flag.** A teacher's session cannot
 * append events (R-3): the server refuses it, because a teacher is not the instrument and a class
 * comparison must rest on a record no interested party can author. So the recorder here is wired to
 * `DiscardingTransport` and `MemoryOutboxStore` — the events are produced, consumed and dropped, and
 * nothing reaches the network or the device.
 *
 * The screen used to say so in two lines and no longer does, at the founder's request. Nothing about
 * the guarantee changed — it was never held by the copy — and the case for saying it was thin: this
 * screen is reached from a button on the teacher's own roster, and no student is selected for a run to
 * be attributed to.
 *
 * A separate surface from `PlaySurface` rather than a mode on it, for the same reason: the guarantee
 * is visible in the wiring. A flag would put "does this write to a child's record" inside a boolean
 * that a future edit could pass wrongly, and the failure would be silent in the worst direction.
 * What that costs is a second boot path — and the preview's is genuinely shorter, because it needs no
 * bundle pin, no persistent storage, no `session_start` and no flush on teardown.
 */

/**
 * A synthetic learner id, so the recorder has the shape it requires.
 *
 * It is never registered anywhere and every event carrying it is discarded. Reusing a real student's
 * id would have been the dangerous alternative: those events would look legitimate the moment anybody
 * changed the transport.
 */
const PREVIEW_PROFILE = "00000000-0000-4000-8000-000000000000";

/**
 * The games grouped by the area of the week they teach, in catalogue order.
 *
 * Derived from the objectives rather than written down: every objective names a strand and the task
 * it is practised in, which is the same rule the games screen follows. A task that appeared under
 * two strands would land in the first, and nothing in the bundle does.
 */
function strandSections(bundle: ContentBundle): { label: string; taskIds: string[] }[] {
  const sections: { strand: string; label: string; taskIds: string[] }[] = [];
  for (const objective of bundle.objectives) {
    let section = sections.find((s) => s.strand === objective.strand);
    if (!section) {
      section = { strand: objective.strand, label: strandLabel(objective.strand), taskIds: [] };
      sections.push(section);
    }
    for (const step of objective.steps) {
      if (!sections.some((s) => s.taskIds.includes(step.taskId))) section.taskIds.push(step.taskId);
    }
  }
  return sections.map(({ label, taskIds }) => ({ label, taskIds }));
}

export function PreviewSurface({
  route,
  navigate,
}: {
  route: Route;
  navigate: (next: Route) => void;
}) {
  const [bundle, setBundle] = useState<ContentBundle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastTask, setLastTask] = useState<TaskDefinition | null>(null);
  /** The run reached its last trial. Distinct from "not playing": the route is still a game. */
  const [finished, setFinished] = useState(false);
  /**
   * Bumped to play the same game again.
   *
   * The run is keyed by task id, so re-entering the same one is a no-op — which is right for a route
   * change and wrong for a replay button. The token gives the effect something that actually differs.
   */
  const [runToken, setRunToken] = useState(0);
  const [awaitingTutorial, setAwaitingTutorial] = useState<
    ((choice: TutorialChoice) => void) | null
  >(null);
  const recorderRef = useRef<SessionRecorder | null>(null);
  const playAreaRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    try {
      /**
       * The bundle is read locally rather than asked for.
       *
       * A teacher has no pinned bundle, because a pin exists to keep one CHILD's history comparable
       * (ADR-002) and there is no history here. This is the same exemption the public games shelf
       * takes, and for the same reason: no record is being read against it.
       */
      const version = latestBundleVersion();
      setBundle(getBundle(version));

      recorderRef.current = new SessionRecorder({
        sessionId: crypto.randomUUID(),
        profileId: PREVIEW_PROFILE,
        appVersion: APP_VERSION,
        pinnedBundleVersion: version,
        store: new MemoryOutboxStore(),
        transport: new DiscardingTransport(),
      });
      /**
       * Deliberately no `attachLifecycle` and no `session_start`.
       *
       * Both exist to make a child's record durable across a page being hidden. There is no record
       * here, so attaching them would only arrange for discarded events to be discarded more
       * reliably.
       */
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const holdForTutorial = useCallback(
    () => new Promise<TutorialChoice>((resolve) => setAwaitingTutorial(() => resolve)),
    [],
  );

  const play = useCallback(
    async (task: TaskDefinition, signal: AbortSignal) => {
      const recorder = recorderRef.current;
      const parent = playAreaRef.current;
      if (!recorder || !parent) return;

      setLastTask(task);
      setFinished(false);
      try {
        await runGame({
          canvasParent: parent,
          task,
          /**
           * The next alternate form, exactly as a child gets.
           *
           * **This was pinned to the first form, and the reasoning was wrong.** It said a teacher is
           * looking at the game rather than being measured by it, so rotating forms would only make
           * two demonstrations differ for no reason. Two things are wrong with that. A stimulus is a
           * pure function of `(formId, trialIndex, difficulty)`, so a fixed form means a teacher who
           * opens a game twice sees the identical cards in the identical order, forever — which does
           * not read as "stable", it reads as broken, and that is how it was reported. And for the
           * two letter games the form also decides where in the alphabet a sitting starts, so a
           * pinned form meant the preview could only ever show the first few letters of the alphabet.
           *
           * A teacher previewing a game is trying to find out what their class will meet. Showing
           * them one frozen draw of it answers the wrong question.
           */
          formId: nextForm(task.id, task.measurement.formIds),
          recorder,
          signal,
          holdForTutorial,
        });
        /**
         * The end of the sitting needs a screen of its own, and its absence was a white page.
         *
         * A finished game destroys its Pixi application and removes the canvas from this element. On
         * the child's surface that is invisible, because a result arrives and the reward screen takes
         * over — here nothing did, so a teacher who played a game to the end was left looking at an
         * empty div with a back button floating on it, on the route that still said a game was
         * running. Reported as "the numbers game goes white at the end", and it was exactly that.
         */
        if (!signal.aborted) setFinished(true);
      } catch (err) {
        if (signal.aborted) return;
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [holdForTutorial],
  );

  /**
   * The route starts and stops games, exactly as it does for a child.
   *
   * Same shape as `PlaySurface`: leaving aborts the run and the next game waits for the previous one
   * to tear down, because two Pixi canvases on one element both keep their pointer handlers and the
   * symptom is taps reaching the wrong game.
   */
  const runningRef = useRef<{
    taskId: string;
    token: number;
    controller: AbortController;
    done: Promise<void>;
  } | null>(null);

  useEffect(() => {
    const wanted = route.kind === "previewGame" ? route.taskId : null;
    const previous = runningRef.current;

    if (previous && previous.taskId !== wanted) {
      previous.controller.abort();
      runningRef.current = null;
      setAwaitingTutorial((resolve: ((choice: TutorialChoice) => void) | null) => {
        resolve?.("start");
        return null;
      });
    }
    if (!wanted || !bundle) return;
    if (runningRef.current?.taskId === wanted && runningRef.current.token === runToken) return;
    // A replay of the same game: tear the finished run down before starting its replacement.
    if (runningRef.current) {
      runningRef.current.controller.abort();
      runningRef.current = null;
    }

    const task = bundle.tasks.find((t) => t.id === wanted);
    if (!task) {
      navigate({ kind: "preview" });
      return;
    }

    const controller = new AbortController();
    const started = runningRef.current as { done: Promise<void> } | null;
    const done = (async () => {
      const earlier = previous ?? started;
      if (earlier) await earlier.done.catch(() => undefined);
      if (controller.signal.aborted) return;
      await play(task, controller.signal);
    })();
    runningRef.current = { taskId: wanted, token: runToken, controller, done };
  }, [route, bundle, play, navigate, runToken]);

  useEffect(() => () => runningRef.current?.controller.abort(), []);

  const playing = route.kind === "previewGame";

  return (
    <div className="relative h-full">
      <div
        ref={playAreaRef}
        // `visibility` rather than `display`: Pixi measures this element, and a display:none host
        // reports zero.
        className={`absolute inset-0 touch-manipulation ${playing ? "visible" : "invisible"}`}
      />

      {/*
        * What the teacher sees when the game is over.
        *
        * Two doors and nothing else: play it again, or go back to the shelf. No score — a teacher
        * trying a game is not being measured by it, and nothing here was recorded (R-3).
        */}
      {playing && finished && (
        <div className="absolute inset-0 z-3 grid place-items-center bg-cream/92 p-xl">
          <div className="w-full max-w-[360px] rounded-xl bg-card px-lg py-lg text-center shadow-raised">
            <h2 className="text-title">{lastTask ? taskLabel(lastTask.id) : ""}</h2>
            <p className="mt-sm text-small text-muted">{COPY.teacher.preview.finished}</p>
            <button
              onClick={() => {
                setFinished(false);
                setRunToken((n) => n + 1);
              }}
              className="mt-lg min-h-touch w-full rounded-pill bg-grape px-lg py-md text-heading text-card shadow-tint-grape"
            >
              {COPY.teacher.preview.again}
            </button>
            <button
              onClick={() => window.history.back()}
              className="mt-md block w-full py-sm text-small text-muted"
            >
              {COPY.teacher.preview.back}
            </button>
          </div>
        </div>
      )}

      {playing && !finished && (
        <button
          onClick={() => window.history.back()}
          aria-label={COPY.teacher.preview.back}
          className="top-safe-lg absolute start-lg z-2 grid size-14 place-items-center rounded-pill bg-card/16 text-card backdrop-blur-[8px]"
        >
          {/* An SVG, not a character: `›` is bidi-mirrored and flips inside an RTL element. */}
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
            <path
              d="M9 5l7 7-7 7"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {!playing && error && (
        <p className="p-lg text-center text-muted">
          {fill(COPY.teacher.failed, { message: error })}
        </p>
      )}

      {!playing && bundle && (
        <div className="mx-auto max-w-[560px] px-lg pt-lg">
          <ScreenHeader
            title={COPY.teacher.preview.title}
            subtitle={COPY.teacher.preview.nothingRecorded}
            onBack={() => window.history.back()}
            backLabel={COPY.teacher.preview.back}
          />
        </div>
      )}

      {!playing && bundle && (
        <HomeScreen
          bundle={bundle}
          /**
           * The same seven headings the games screen uses, computed from the bundle in hand.
           *
           * No request for them: this screen already holds the whole bundle, objectives included, so
           * the strand each game belongs to is a lookup rather than a fetch. A teacher browsing
           * twenty-five games to try one has the same question here as on the games screen, and the
           * child's own shelf still gets no headings — it passes nothing and is unchanged.
           */
          sections={strandSections(bundle)}
          /**
           * No profile, which is also how the arm gets pinned.
           *
           * A teacher is not on an A-13 arm. Handing the shelf a synthetic id put them on one by
           * hash, and it landed on the story arm — so the games screen called the game one thing
           * and this screen called it another, which is what a teacher reported. Null picks the
           * first arm, always, and the two screens agree.
           */
          profileId={null}
          // No class, so nothing to be restricted by: a teacher sees every game (ADR-014).
          openTasks={null}
          onPlay={(task) => navigate({ kind: "previewGame", taskId: task.id })}
          /**
           * No suggestion card, and no sign-out.
           *
           * "What to play next" is a fact about a child, and there is no child here. A teacher's
           * sign-out lives on the roster, where the rest of their account controls are.
           */
          next={null}
          onPlayNext={(task) => navigate({ kind: "previewGame", taskId: task.id })}
          onSignOut={() => window.history.back()}
          hideChrome
        />
      )}

      {awaitingTutorial && lastTask && (
        <TutorialPrompt
          family={lastTask.family}
          onChoose={(choice) => {
            awaitingTutorial(choice);
            setAwaitingTutorial(null);
          }}
        />
      )}
    </div>
  );
}
